import { useEffect, useRef } from 'react';
import { X, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { usePostHog } from '@posthog/react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import CategoryFilter from '@/redux/category/CategoryFilter';
import useSearchParamActions from '@/customHooks/useSearchParamActions';
import PostsFilterByDate from '@/redux/post/PostsFilterByDate';
import ToolTypeFilter from '@/redux/post/ToolTypeFilter';
import ControllerTypeFilter from '@/redux/post/ControllerTypeFilter';
import SoftwareFilter from '@/redux/post/SoftwareFilter';
import PartnerFilter from '@/redux/post/PartnerFilter';
import RatingFilter from '@/redux/post/RatingFilter';
import SortControl from '@/redux/post/SortControl';
import ContentTypeFilter from '@/redux/post/ContentTypeFilter';
import { useGetPartnersQuery } from '@/redux/partner/partner.api.slice';
import sortLabel from './sortLabel';

const FILTER_ONLY_PARAMS = [
  'category',
  'postedAt',
  'author',
  'toolType',
  'controllerType',
  'partner',
  'ratings',
  'contentType',
  'softwareUsed',
];

const ALL_FILTER_PARAMS = [...FILTER_ONLY_PARAMS, 'sortBy', 'sortOrder'];

function useFilterAnalytics(activeKeys: string[]) {
  const posthog = usePostHog();
  const seen = useRef<Set<string>>(new Set());
  const key = activeKeys.join('|');

  useEffect(() => {
    if (!import.meta.env.VITE_POSTHOG_PROJECT_TOKEN) return;
    const now = new Set(activeKeys);
    now.forEach((k) => {
      if (seen.current.has(k)) return;
      posthog.capture('feed_filter_applied', {
        filter: k,
        surface: 'filter_menu',
      });
    });
    seen.current = now;
    // activeKeys is rebuilt every render; `key` is its stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, posthog]);
}

function useFilterState() {
  const {
    getAllSearchParams,
    removeSearchParam,
    removeAllSearchParams,
    getSearchParam,
  } = useSearchParamActions();
  const searchParams = getAllSearchParams();

  const currentSortBy = getSearchParam('sortBy');
  const currentSortOrder = getSearchParam('sortOrder');

  const filterSearchParams = searchParams.filter(
    (param) =>
      FILTER_ONLY_PARAMS.includes(param.key) &&
      !['sortBy', 'sortOrder'].includes(param.key)
  );

  const sortDisplayLabel = sortLabel(currentSortBy, currentSortOrder);

  const activeKeys = [
    ...filterSearchParams.map((p) => p.key),
    ...(currentSortBy ? ['sortBy'] : []),
  ];

  return {
    searchParams,
    filterSearchParams,
    sortDisplayLabel,
    activeKeys,
    removeSearchParam,
    removeAllSearchParams,
  };
}

/** Filter controls that sit inline with the feed search input. */
export function FeedFilterControls() {
  const posthog = usePostHog();
  const { filterSearchParams, activeKeys } = useFilterState();

  useFilterAnalytics(activeKeys);

  const filterCount = filterSearchParams.reduce(
    (acc, p) => acc + p.value.length,
    0
  );

  const openMenu = () => {
    if (!import.meta.env.VITE_POSTHOG_PROJECT_TOKEN) return;
    posthog.capture('feed_filters_more_opened');
  };

  const filterTrigger = (
    <Button variant="outline" className="gap-2" onClick={openMenu}>
      <SlidersHorizontal className="h-4 w-4" />
      Filter
      {filterCount > 0 && (
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
          {filterCount}
        </span>
      )}
      <ChevronDown className="h-4 w-4" />
    </Button>
  );

  const filters = (submenu?: boolean) => (
    <>
      <CategoryFilter submenu={submenu} />
      <ToolTypeFilter submenu={submenu} />
      <ContentTypeFilter submenu={submenu} />
      <ControllerTypeFilter submenu={submenu} />
      {submenu && <DropdownMenuSeparator />}
      <PostsFilterByDate submenu={submenu} />
      <SoftwareFilter submenu={submenu} />
      <PartnerFilter submenu={submenu} />
      <RatingFilter submenu={submenu} />
    </>
  );

  return (
    <>
      {/* Desktop: every filter as a submenu of one button, sort beside it */}
      <div
        data-testid="feed-filter-controls"
        className="hidden items-center gap-2 md:flex"
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{filterTrigger}</DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {filters(true)}
          </DropdownMenuContent>
        </DropdownMenu>
        <SortControl />
      </div>

      {/* Mobile: the same set stacked in a sheet, where submenus do not sit well */}
      <div className="flex items-center gap-2 md:hidden">
        <Sheet>
          <SheetTrigger asChild>{filterTrigger}</SheetTrigger>
          <SheetContent side="bottom" className="flex h-[70vh] flex-col">
            <SheetHeader className="flex-shrink-0 pb-4 text-left">
              <SheetTitle>Filter</SheetTitle>
            </SheetHeader>
            <div className="flex flex-1 flex-col gap-3 overflow-y-auto pb-6">
              {filters()}
            </div>
          </SheetContent>
        </Sheet>
        <SortControl />
      </div>
    </>
  );
}

function SortChip({
  label,
  onClear,
}: {
  label: string | null;
  onClear: () => void;
}) {
  if (!label) return null;
  return (
    <Button variant="secondary" size="sm" className="gap-2">
      {label}
      <X className="h-4 w-4" onClick={onClear} />
    </Button>
  );
}

/** Active-filter chips, shown under the search row only when something is set. */
export function FeedFilterChips() {
  const partnerQuery = useGetPartnersQuery();
  const {
    filterSearchParams,
    sortDisplayLabel,
    removeSearchParam,
    removeAllSearchParams,
  } = useFilterState();

  const getFilterDisplayValue = (key: string, value: string): string => {
    if (key === 'contentType') {
      const labels: Record<string, string> = {
        post: 'Single Workpiece',
        projectGroup: 'Multi Workpiece',
      };
      return labels[value] || value;
    }
    if (key === 'partner' && partnerQuery.data) {
      return partnerQuery.data.find((p) => p.user === value)?.name || value;
    }
    return value;
  };

  const clearSort = () => {
    removeSearchParam('sortBy');
    removeSearchParam('sortOrder');
  };

  const chipCount = filterSearchParams.length + (sortDisplayLabel ? 1 : 0);
  if (chipCount === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 pt-2">
      <SortChip label={sortDisplayLabel} onClear={clearSort} />
      {filterSearchParams.map((param) =>
        param.value.map((value) => (
          <Button
            key={`${param.key}-${value}`}
            variant="secondary"
            size="sm"
            className="gap-2"
          >
            {getFilterDisplayValue(param.key, value)}
            <X
              className="h-4 w-4"
              onClick={() => removeSearchParam(param.key, value)}
            />
          </Button>
        ))
      )}
      {chipCount > 1 && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => removeAllSearchParams(ALL_FILTER_PARAMS)}
        >
          Clear All
        </Button>
      )}
      <Separator className="my-1 w-full" />
    </div>
  );
}
